import { AuthorizedBaseAxios, flatten } from 'core/axios/axios';
import { evolve, map, pipe } from 'ramda';
import {
  BusinessPartner,
  BusinessPartnerDataDTO,
  BusinessPartnerDTO,
  mapToBusinessPartner,
} from 'models/businessPartner';
import { PageParams, PageWrapperDTO } from 'models/page';
import { Entity } from 'models/entity';
import {
  mapToPartnerHigoDevice,
  PartnerHigoDeviceDTO,
} from 'models/higoDevice';
import {
  Doctor,
  DoctorDTO,
  DoctorFormModel,
  RegisterBusinessPartnerDoctorDTO,
} from 'models/doctor';
import {
  ProfileOwnerDTO,
  ProfileOwnerFormModel,
  ProfileOwnerTransferOption,
  RegisterBusinessPartnerProfileOwnerDTO,
} from 'models/user';
import { format, parseISO } from 'date-fns';
import { AdminUserDTO, mapToAdminUser } from 'models/adminUser';
import { purge } from 'lib/ramda/purge';
import { DeleteUserStatus } from 'models/deleteUserStatus';
import {
  PatientDTO,
  BusinessPartnerPatientFormModel,
  PatientType,
} from 'models/patient';
import {
  MedicalOperatorDTO,
  MedicalOperatorFormModel,
} from 'models/medicalOperator';
import { RegisterBusinessPartnerAdminUserFormModel } from 'pages/BusinessPartnerManagement/BusinessPartnerAdminsManagement/BusinessPartnerRegisterAdminPage';
import { ImportFileDTO } from 'pages/BusinessPartnerManagement/BusinessPartnerPatientsManagement/shared/BusinessPartnerImportPatientsModal';
import { formDataFromObj } from '@summer/react-kit/functions';
import { ListWrapper, unwrap } from '@summer/react-kit';
import { formatUserName } from 'common/formatUserNames';
import { emptyStringToNull } from 'lib/ramda/emptyStringToNull';
import { BusinessPartnerFormModel } from 'pages/BusinessPartnerManagement/BusinessPartnerForm';

const businessPartnersResUrl = `api/businessPartners`;

export interface BusinessPartnerMutator<T> {
  businessPartnerId: number;
  dto: T;
}

export interface UserPermissionsMutator {
  userId: number;
  items: UserPermissionsDTO[];
}

export interface UserPermissionsDTO {
  permission: string;
  scope: string;
  enabled?: boolean;
  read?: boolean;
  create?: boolean;
  update?: boolean;
  delete?: boolean;
}

export interface DemoAccountDTO {
  dateOfBirth: string;
  email: string;
  examsLimit?: number;
  firstName: string;
  gender: string;
  idNumber?: string;
  idNumberDocumentType?: string;
  jobTitle?: string;
  lastName: string;
  licenceId?: string;
  medicalSpecialization?: string;
  phoneNumber?: string;
  prefixCode?: string;
  profilesLimit?: number;
}
export interface BusinessPartnerMedicalOperatorMutator<T>
  extends BusinessPartnerMutator<T> {
  medicalOperatorId: number;
}
export interface BusinessPartnerId {
  businessPartnerId: number;
}

export interface DeleteUserProps {
  userId: number;
  status?: DeleteUserStatus;
}

export interface DeleteAdminProps {
  adminId: number;
  businessPartnerId?: number;
}

export interface DeleteMedicalOperatorPatientProps {
  id: number;
  medicalOperatorId: number;
  status?: DeleteUserStatus;
}

export interface BusinessPartnerDevicesPageParams extends PageParams {
  assigned?: boolean;
  deviceIds?: number[];
  serialNumber?: string;
  orderBy?:
    | 'EXAM_UPDATE_DATE'
    | 'DIAGNOSE_DATE'
    | 'DEVICE_CREATE_DATE'
    | 'DEVICE_SERIAL_NUMBER';
  modeType?: 'FAMILY_MODE' | 'PRO_MODE';
  partnerName?: string;
  subPartnersIds?: string;
}

export type AdditionalDeviceType = 'BP_MONITOR' | 'OXIMETER';

export interface BusinessPartnerAdditionalDevicesPageParams extends PageParams {
  serialNumber?: string;
  type?: AdditionalDeviceType;
  'rowBounds.limit'?: number;
  'rowBounds.offset'?: number;
}

export interface SuperAdminAdditionalDevicesPageParams
  extends BusinessPartnerAdditionalDevicesPageParams {
  partnerIds?: number[];
}

export interface BusinessPartnerAdditionalDeviceDTO {
  id: number;
  serialNumber?: string;
  deviceType?: AdditionalDeviceType;
  type?: AdditionalDeviceType;
  lastConnection?: string;
  lastMessageTime?: string;
  partnerName?: string;
  partner?: {
    name?: string;
  };
}

export interface BusinessPartnerAdditionalDevice {
  id: number;
  serialNumber: string | null;
  deviceType: string | null;
  lastMessageTime: string | null;
  partnerName: string | null;
}

export interface BusinessPartnerAdminsPageParams extends PageParams {
  login?: string;
  subPartnersIds?: string;
}

export interface BusinessPartnerDoctorsPageParams extends PageParams {
  firstName?: string;
  lastName?: string;
  subPartnersIds?: string;
}

export interface BusinessPartnerProfileOwnersPageParams extends PageParams {
  firstName?: string;
  lastName?: string;
  assignedDevice?: boolean;
  subPartnersIds?: string;
}

export interface BusinessPartnerPatientsPageParams extends PageParams {
  firstName?: string;
  lastName?: string;
  assignedDevice?: boolean;
  partnerId?: number;
  patientType?: PatientType;
  subPartnersIds?: string;
}

export interface BusinessPartnerMedicalOperatorsPageParams extends PageParams {
  firstName?: string;
  lastName?: string;
  email?: string;
  subPartnersIds?: string;
}

export interface BusinessPartnersPageParams extends PageParams {
  name?: string;
  uniqueNumber?: string;
  description?: string;
}

export interface AssignPatientsToMedicalOperatorParams {
  items: number[];
  medicalOperatorId: number;
}
export interface TransferPatientsToMedicalOperator {
  patientIds: number[];
  medicalOperatorId: number;
  assignMedicalOperatorId: number;
}

export interface TransferProfileOwnerDTO {
  userId: number;
  newPartnerId: number;
  shiftOption: ProfileOwnerTransferOption;
}

const fetch = (params: BusinessPartnersPageParams) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<BusinessPartnerDTO>>(
    `${businessPartnersResUrl}`,
    {
      params: purge(params),
    }
  ).then(pipe(flatten, evolve({ content: map(mapToBusinessPartner) })));

const fetchWithoutContextPartner = (
  businessPartnerId: number,
  userId: number,
  params: BusinessPartnersPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<BusinessPartnerDTO>>(
    `${businessPartnersResUrl}/${businessPartnerId}/users/${userId}/shift/partners`,
    {
      params: purge(params),
    }
  ).then(pipe(flatten, evolve({ content: map(mapToBusinessPartner) })));

const create = (dto: BusinessPartnerDataDTO) =>
  AuthorizedBaseAxios.post<BusinessPartnerDTO>(
    `${businessPartnersResUrl}`,
    dto
  ).then(pipe(flatten, mapToBusinessPartner));

const fetchById = (id: number) => {
  return AuthorizedBaseAxios.get<BusinessPartnerDTO>(
    `${businessPartnersResUrl}/${id}`
  ).then(pipe(flatten, mapToBusinessPartner));
};

const update = (entity: Entity & BusinessPartnerDataDTO) => {
  const { id, ...body } = entity;
  const cleanBody = JSON.parse(JSON.stringify(body));
  return AuthorizedBaseAxios.put(
    `${businessPartnersResUrl}/${id}`,
    cleanBody
  ).then(pipe(flatten, mapToBusinessPartner));
};

const deleteEntity = (entity: BusinessPartner) =>
  AuthorizedBaseAxios.delete(`${businessPartnersResUrl}/${entity.id}`);

const updateUserPermissions = ({ userId, items }: UserPermissionsMutator) =>
  AuthorizedBaseAxios.put(`/api/permission/${userId}`, { items: items }).then(
    pipe(flatten)
  );

const fetchUserPermissions = (userId: number) =>
  AuthorizedBaseAxios.get(`/api/permission/${userId}`).then(pipe(flatten));

const fetchBusinessPartnerPlans = () =>
  AuthorizedBaseAxios.get<ListWrapper<string>>(
    `${businessPartnersResUrl}/plans`
  ).then(pipe(flatten, unwrap));

const fetchDevices = (
  businessPartnerId: number,
  params: BusinessPartnerDevicesPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<PartnerHigoDeviceDTO>>(
    `${businessPartnersResUrl}/${businessPartnerId}/devices`,
    {
      params,
    }
  ).then(pipe(flatten, evolve({ content: map(mapToPartnerHigoDevice) })));

const mapToBusinessPartnerAdditionalDevice = (
  dto: BusinessPartnerAdditionalDeviceDTO
): BusinessPartnerAdditionalDevice => ({
  id: dto.id,
  serialNumber: dto.serialNumber ?? null,
  deviceType: dto.deviceType ?? dto.type ?? null,
  lastMessageTime: dto.lastConnection ?? dto.lastMessageTime ?? null,
  partnerName: dto.partnerName ?? dto.partner?.name ?? null,
});

const fetchAdditionalDevicesForLocaAdmin = (
  businessPartnerId: number,
  params: BusinessPartnerAdditionalDevicesPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<BusinessPartnerAdditionalDeviceDTO>>(
    `/api/partners/${businessPartnerId}/additionalDevices`,
    {
      params: purge(params),
    }
  ).then(
    pipe(
      flatten,
      evolve({ content: map(mapToBusinessPartnerAdditionalDevice) })
    )
  );

const fetchAdditionalDevicesForSuperAdmin = (
  params: SuperAdminAdditionalDevicesPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<BusinessPartnerAdditionalDeviceDTO>>(
    `/api/additionalDevices`,
    {
      params: purge(params),
    }
  ).then(
    pipe(
      flatten,
      evolve({ content: map(mapToBusinessPartnerAdditionalDevice) })
    )
  );

const fetchAdmins = (
  businessPartnerId: number,
  params: BusinessPartnerAdminsPageParams
) => {
  return AuthorizedBaseAxios.get<PageWrapperDTO<AdminUserDTO>>(
    `${businessPartnersResUrl}/${businessPartnerId}/admins`,
    {
      params: purge(params),
    }
  ).then(pipe(flatten, evolve({ content: map(mapToAdminUser) })));
};

const fetchSubpartners = (
  partnerId: number,
  params?: BusinessPartnersPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<BusinessPartnerDTO>>(
    `${businessPartnersResUrl}/${partnerId}/subpartners`,
    {
      params,
    }
  ).then(pipe(flatten, evolve({ content: map(mapToBusinessPartner) })));

const fetchPartnerMetaData = (partnerIds: string) =>
  AuthorizedBaseAxios.get<BusinessPartnerDTO>(
    `${businessPartnersResUrl}/metadata`,
    { params: { partnerIds: partnerIds } }
  );

const activatePartner = (partnerId: number) =>
  AuthorizedBaseAxios.patch(`${businessPartnersResUrl}/${partnerId}/activate`);

const deactivatePartner = (partnerId: number) =>
  AuthorizedBaseAxios.patch(
    `${businessPartnersResUrl}/${partnerId}/deactivate`
  );

export const createSubpartner = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<BusinessPartnerFormModel>) =>
  AuthorizedBaseAxios.post<BusinessPartnerDTO>(
    `${businessPartnersResUrl}/${businessPartnerId}/subpartners`,
    dto
  ).then(pipe(flatten));

const deleteAdminBySuperAdmin = ({ adminId }: DeleteAdminProps) =>
  AuthorizedBaseAxios.delete(`/api/management/admins/${adminId}`);

const deleteAdminByLocalAdmin = ({
  businessPartnerId,
  adminId,
}: DeleteAdminProps) =>
  AuthorizedBaseAxios.delete(
    `${businessPartnersResUrl}/${businessPartnerId}/admins/${adminId}`
  );

const registerNewAdminUser = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<RegisterBusinessPartnerAdminUserFormModel>) =>
  AuthorizedBaseAxios.post<AdminUserDTO>(
    `${businessPartnersResUrl}/${businessPartnerId}/admins`,
    dto
  ).then(pipe(flatten));

const registerNewDemoAccount = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<DemoAccountDTO>) =>
  AuthorizedBaseAxios.post<DemoAccountDTO>(
    `${businessPartnersResUrl}/${businessPartnerId}/demoAccount`,
    dto
  ).then(pipe(flatten));

const createPartnerIntegrationAccount = ({
  businessPartnerId,
  sdkClient,
}: { sdkClient: boolean } & BusinessPartnerId) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/users/integration?sdkClient=${sdkClient}`
  );

const fetchDoctors = (
  businessPartnerId: number,
  params: BusinessPartnerDoctorsPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<Doctor>>(
    `${businessPartnersResUrl}/${businessPartnerId}/doctors`,
    {
      params,
    }
  ).then(pipe(flatten));

const fetchDoctorById = (businessPartnerId: number, doctorId: number) =>
  AuthorizedBaseAxios.get<DoctorDTO>(
    `${businessPartnersResUrl}/${businessPartnerId}/doctors/${doctorId}`
  ).then(pipe(flatten));

const modifyDoctorById = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<DoctorFormModel>) =>
  AuthorizedBaseAxios.put(
    `${businessPartnersResUrl}/${businessPartnerId}/doctors/${dto.userId}`,
    dto
  ).then(pipe(flatten));

const registerNewDoctor = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<RegisterBusinessPartnerDoctorDTO>) =>
  AuthorizedBaseAxios.post<DoctorDTO>(
    `${businessPartnersResUrl}/${businessPartnerId}/doctors`,
    formatUserName(dto)
  ).then(pipe(flatten));

const deleteDoctor = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<DeleteUserProps>) =>
  AuthorizedBaseAxios.delete(
    `${businessPartnersResUrl}/${businessPartnerId}/doctors/${dto.userId}`,
    { params: { status: dto.status ?? DeleteUserStatus.Archive } }
  ).then(pipe(flatten));

const resetDoctorPassword = ({
  businessPartnerId,
  id,
}: { id: number } & BusinessPartnerId) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/doctors/${id}/password`
  );

const fetchProfileOwners = (
  businessPartnerId: number,
  params: BusinessPartnerProfileOwnersPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<ProfileOwnerDTO>>(
    `${businessPartnersResUrl}/${businessPartnerId}/users`,
    { params }
  ).then(pipe(flatten));

const fetchProfileOwnerById = (businessPartnerId: number, userId: number) =>
  AuthorizedBaseAxios.get<ProfileOwnerDTO>(
    `${businessPartnersResUrl}/${businessPartnerId}/users/${userId}`
  ).then(pipe(flatten));

const modifyProfileOwnerById = ({
  dto,
}: BusinessPartnerMutator<ProfileOwnerFormModel>) =>
  AuthorizedBaseAxios.put(`api/users/data`, dto).then(pipe(flatten));

const transferProfileOwnerToOtherPartner = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<TransferProfileOwnerDTO>) =>
  AuthorizedBaseAxios.put(
    `${businessPartnersResUrl}/${businessPartnerId}/users/${dto.userId}/shift`,
    { newPartnerId: dto.newPartnerId, shiftOption: dto.shiftOption }
  );

const registerNewUser = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<RegisterBusinessPartnerProfileOwnerDTO>) =>
  AuthorizedBaseAxios.post<ProfileOwnerDTO>(
    `${businessPartnersResUrl}/${businessPartnerId}/users`,
    formatUserName({
      ...dto,
    })
  ).then(pipe(flatten));

const deleteUser = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<DeleteUserProps>) =>
  AuthorizedBaseAxios.delete(
    `${businessPartnersResUrl}/${businessPartnerId}/users/${dto.userId}`,
    { params: { status: dto.status ?? DeleteUserStatus.Archive } }
  ).then(pipe(flatten));

const resetProfileOwnerPassword = ({
  businessPartnerId,
  id,
}: { id: number } & BusinessPartnerId) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/users/${id}/password`
  );

const assignDeviceToUser = ({
  businessPartnerId,
  deviceId,
  userId,
}: { deviceId: number; userId: number } & BusinessPartnerId) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/devices/${deviceId}/user`,
    {
      userId,
    }
  );

const unassignUserDevice = ({
  businessPartnerId,
  deviceId,
  userId,
}: { deviceId: number; userId: number } & BusinessPartnerId) =>
  AuthorizedBaseAxios.delete(
    `${businessPartnersResUrl}/${businessPartnerId}/devices/${deviceId}/user/${userId}`
  );

// todo: change to business partner patient controller if available
const fetchPatients = (
  businessPartnerId: number,
  params: BusinessPartnerPatientsPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<PatientDTO>>(
    `/api/management/patients`,
    { params: { ...params, partnerId: businessPartnerId } }
  ).then(pipe(flatten));

export const fetchPatientsAvailableToAssign = (
  partnerId: number,
  medicalOperatorId: number,
  params: {
    page?: number;
    pageSize?: number;
    firstName?: string;
    lastName?: string;
  }
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<PatientDTO>>(
    `/api/businessPartners/${partnerId}/medicalOperators/${medicalOperatorId}/patients/assignment/available`,
    { params }
  ).then(pipe(flatten));

const registerNewPatient = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<BusinessPartnerPatientFormModel>) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/patients`,
    formatUserName(
      purge({
        ...dto,
        dateOfBirth: format(parseISO(dto.dateOfBirth), 'yyyy-MM-dd'),
      })
    )
  ).then(pipe(flatten));

const registerNewMedicalOperatorsPatient = ({
  businessPartnerId,
  medicalOperatorId,
  dto,
}: BusinessPartnerMedicalOperatorMutator<BusinessPartnerPatientFormModel>) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${medicalOperatorId}/patients`,
    purge(
      formatUserName({
        ...dto,
        dateOfBirth: format(parseISO(dto.dateOfBirth), 'yyyy-MM-dd'),
      })
    )
  ).then(pipe(flatten));

const fetchPatientById = (businessPartnerId: number, patientId: number) =>
  AuthorizedBaseAxios.get(
    `${businessPartnersResUrl}/${businessPartnerId}/patients/${patientId}`
  ).then(pipe(flatten));

const modifyPatientById = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<BusinessPartnerPatientFormModel>) =>
  AuthorizedBaseAxios.put(
    `${businessPartnersResUrl}/${businessPartnerId}/patients/${dto.id}`,
    emptyStringToNull({
      ...dto,
      dateOfBirth: format(parseISO(dto.dateOfBirth), 'yyyy-MM-dd'),
    })
  ).then(pipe(flatten));

const fetchMedicalOperators = (
  businessPartnerId: number,
  params: BusinessPartnerMedicalOperatorsPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<MedicalOperatorDTO>>(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators`,
    { params }
  ).then(pipe(flatten));

const registerNewMedicalOperator = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<MedicalOperatorFormModel>) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators`,
    formatUserName(dto)
  ).then(pipe(flatten));

const fetchMedicalOperatorById = (
  businessPartnerId: number,
  medicalOperatorId: number
) =>
  AuthorizedBaseAxios.get(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${medicalOperatorId}`
  ).then(pipe(flatten));

const modifyMedicalOperatorById = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<MedicalOperatorFormModel>) =>
  AuthorizedBaseAxios.put(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${dto.userId}`,
    dto
  ).then(pipe(flatten));

const deleteMedicalOperator = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<DeleteUserProps>) =>
  AuthorizedBaseAxios.delete(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${dto.userId}`,
    { params: { status: dto.status ?? DeleteUserStatus.Archive } }
  );

const fetchMedicalOperatorPatients = (
  businessPartnerId: number,
  medicalOperatorId: number,
  params: BusinessPartnerProfileOwnersPageParams
) =>
  AuthorizedBaseAxios.get<PageWrapperDTO<PatientDTO>>(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${medicalOperatorId}/patients`,
    { params: { ...params } }
  ).then(pipe(flatten));

const deleteMedicalOperatorPatient = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<DeleteMedicalOperatorPatientProps>) =>
  AuthorizedBaseAxios.delete(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${dto.medicalOperatorId}/patients/${dto.id}`
  );

const unassignMedicalOperatorPatients = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<AssignPatientsToMedicalOperatorParams>) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${dto.medicalOperatorId}/bulk/patients/unassignment`,
    { items: dto.items }
  ).then(pipe(flatten));

const assignPatientsToMedicalOperator = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<AssignPatientsToMedicalOperatorParams>) =>
  AuthorizedBaseAxios.post(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${dto.medicalOperatorId}/patients/assignment`,
    { items: dto.items }
  ).then(pipe(flatten));

const transferPatientsToMedicalOperator = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<TransferPatientsToMedicalOperator>) =>
  AuthorizedBaseAxios.put(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${dto.medicalOperatorId}/patients/reassignment`,
    {
      patientIds: dto.patientIds,
      assignMedicalOperatorId: dto.assignMedicalOperatorId,
    }
  ).then(pipe(flatten));

const importPatientsFromFile = ({
  businessPartnerId,
  dto,
}: BusinessPartnerMutator<ImportFileDTO>) =>
  AuthorizedBaseAxios.post<ListWrapper<number>>(
    `${businessPartnersResUrl}/${businessPartnerId}/patients/csv`,
    formDataFromObj({ file: dto.file }),
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }
  ).then(pipe(flatten, unwrap));

const importPatientsFromFileToMedicalOperator = ({
  businessPartnerId,
  medicalOperatorId,
  dto,
}: BusinessPartnerMedicalOperatorMutator<ImportFileDTO>) =>
  AuthorizedBaseAxios.post<ListWrapper<number>>(
    `${businessPartnersResUrl}/${businessPartnerId}/medicalOperators/${medicalOperatorId}/patients/csv`,
    formDataFromObj({ file: dto.file }),
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }
  ).then(pipe(flatten, unwrap));

export interface CreateAdditionalDeviceDTO {
  serialNumber: string;
  deviceType: 'OXIMETER' | 'BP_MONITOR';
}

export interface AssignAdditionalDeviceToPartnerParams {
  additionalDeviceId: number;
  partnerId: number;
}

const createAdditionalDevice = (dto: CreateAdditionalDeviceDTO) =>
  AuthorizedBaseAxios.post('/api/additionalDevices', dto);

const assignAdditionalDeviceToPartner = ({
  additionalDeviceId,
  partnerId,
}: AssignAdditionalDeviceToPartnerParams) =>
  AuthorizedBaseAxios.put(
    `/api/additionalDevices/${additionalDeviceId}/partner/${partnerId}`
  );

export const BusinessPartnersAPI = {
  fetch,
  fetchById,
  update,
  deleteEntity,
  updateUserPermissions,
  fetchUserPermissions,
  fetchBusinessPartnerPlans,
  create,
  fetchDevices,
  fetchAdditionalDevicesForLocaAdmin,
  fetchAdditionalDevicesForSuperAdmin,
  createAdditionalDevice,
  assignAdditionalDeviceToPartner,
  fetchAdmins,
  fetchSubpartners,
  fetchPartnerMetaData,
  activatePartner,
  registerNewDemoAccount,
  deactivatePartner,
  createPartnerIntegrationAccount,
  createSubpartner,
  deleteAdminBySuperAdmin,
  deleteAdminByLocalAdmin,
  fetchDoctors,
  fetchDoctorById,
  fetchProfileOwners,
  fetchProfileOwnerById,
  fetchPatientById,
  registerNewDoctor,
  registerNewUser,
  registerNewPatient,
  modifyPatientById,
  deleteDoctor,
  deleteUser,
  resetDoctorPassword,
  resetProfileOwnerPassword,
  fetchPatients,
  assignDeviceToUser,
  unassignUserDevice,
  fetchMedicalOperators,
  registerNewMedicalOperator,
  fetchMedicalOperatorById,
  modifyMedicalOperatorById,
  deleteMedicalOperator,
  fetchMedicalOperatorPatients,
  deleteMedicalOperatorPatient,
  assignPatientsToMedicalOperator,
  registerNewMedicalOperatorsPatient,
  registerNewAdminUser,
  importPatientsFromFile,
  importPatientsFromFileToMedicalOperator,
  transferProfileOwnerToOtherPartner,
  fetchWithoutContextPartner,
  unassignMedicalOperatorPatients,
  transferPatientsToMedicalOperator,
  modifyDoctorById,
  modifyProfileOwnerById,
  fetchPatientsAvailableToAssign,
};
